import { DeleteOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { type ILanguageType } from '@mahawi/eshop-common/src/types';
import {
  Badge,
  Button,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import { getCountryOptions } from '../fragments/get-country-options';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { countriesLoadAll } from '../reducers/countries/reducer';
import {
  countryLanguagesAdd,
  countryLanguagesLoad,
  countryLanguagesLoadAllLanguages,
  countryLanguagesRemove,
  countryLanguagesSetPrimary,
} from '../reducers/country-languages/reducer';
import { type ICountryLanguageEntry } from '../reducers/country-languages/types';
import { SPACING } from '../theme/tokens';

interface DataType extends ICountryLanguageEntry {
  key: string;
}

function SettingsCountryLanguagesContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const countriesCountries = useSelector(
    (s: RootState) => s.Countries.countries,
  );
  const countryLanguagesLanguages = useSelector(
    (s: RootState) => s.CountryLanguages.languages,
  );
  const countryLanguagesAllLanguages = useSelector(
    (s: RootState) => s.CountryLanguages.allLanguages,
  );
  const countryLanguagesInProcess = useSelector(
    (s: RootState) => s.CountryLanguages.inProcess,
  );

  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(
    undefined,
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(
    undefined,
  );

  useEffect((): void => {
    if (!countriesCountries) {
      dispatch(countriesLoadAll());
    }
  }, [countriesCountries, dispatch]);

  useEffect((): void => {
    dispatch(countryLanguagesLoadAllLanguages());
  }, [dispatch]);

  useEffect((): void => {
    if (!selectedCountry) {
      return;
    }
    dispatch(countryLanguagesLoad(selectedCountry));
  }, [selectedCountry, dispatch]);

  const handleAdd = useCallback((): void => {
    if (!selectedCountry || !selectedLanguage) {
      return;
    }
    const isFirst: boolean = countryLanguagesLanguages.length === 0;
    dispatch(countryLanguagesAdd(selectedCountry, selectedLanguage, isFirst));
    setSelectedLanguage(undefined);
  }, [selectedCountry, selectedLanguage, countryLanguagesLanguages, dispatch]);

  const handleRemove = useCallback(
    (languageCode: string): void => {
      if (!selectedCountry) {
        return;
      }
      dispatch(countryLanguagesRemove(selectedCountry, languageCode));
    },
    [selectedCountry, dispatch],
  );

  const handleSetPrimary = useCallback(
    (languageCode: string): void => {
      if (!selectedCountry) {
        return;
      }
      dispatch(countryLanguagesSetPrimary(selectedCountry, languageCode));
    },
    [selectedCountry, dispatch],
  );

  const assignedCodes: Set<string> = new Set(
    countryLanguagesLanguages.map((l: ICountryLanguageEntry): string => l.code),
  );

  const availableToAdd: ILanguageType[] = countryLanguagesAllLanguages.filter(
    (l: ILanguageType): boolean => !assignedCodes.has(l.code),
  );

  const columns: ColumnsType<DataType> = [
    {
      title: 'Language',
      dataIndex: 'name',
      render: (name: string, record: DataType): React.ReactElement => (
        <Space>
          {name}
          <Typography.Text type="secondary">({record.code})</Typography.Text>
          {record.isPrimary && <Badge status="success" text="Primary" />}
        </Space>
      ),
    },
    {
      title: 'Actions',
      width: 200,
      render: (_: unknown, record: DataType): React.ReactElement => (
        <Space>
          {!record.isPrimary && (
            <Button
              size="small"
              icon={<StarOutlined />}
              onClick={(): void => handleSetPrimary(record.code)}
              loading={countryLanguagesInProcess}
            >
              Set primary
            </Button>
          )}
          {record.isPrimary && (
            <Button size="small" icon={<StarFilled />} disabled>
              Primary
            </Button>
          )}
          <Popconfirm
            placement="bottom"
            title="Remove this language from the country?"
            okText="Yes"
            cancelText="No"
            onConfirm={(): void => handleRemove(record.code)}
            disabled={record.isPrimary}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={countryLanguagesInProcess}
              disabled={record.isPrimary}
            >
              Remove
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const dataSource: DataType[] = countryLanguagesLanguages.map(
    (l: ICountryLanguageEntry): DataType => ({ ...l, key: l.code }),
  );

  return (
    <Space orientation="vertical" size={SPACING.xl} style={{ width: '100%' }}>
      <DetailPageHeader title="Country languages" />

      <Space>
        <Select
          placeholder="Select country"
          style={{ width: 240 }}
          value={selectedCountry}
          onChange={(value: string): void => setSelectedCountry(value)}
          showSearch={{ optionFilterProp: 'label' }}
          options={getCountryOptions(countriesCountries, 'isoCode2')}
        />
      </Space>

      {selectedCountry && (
        <>
          <Table
            columns={columns}
            dataSource={dataSource}
            loading={countryLanguagesInProcess}
            pagination={false}
            rowKey="code"
            size="small"
          />

          <Space>
            <Select
              placeholder="Add language"
              style={{ width: 240 }}
              value={selectedLanguage}
              onChange={(value: string): void => setSelectedLanguage(value)}
              showSearch={{ optionFilterProp: 'label' }}
              options={availableToAdd.map(
                (l: ILanguageType): { value: string; label: string } => ({
                  value: l.code,
                  label: `${l.name} (${l.code})`,
                }),
              )}
            />
            <Button
              type="primary"
              disabled={!selectedLanguage || countryLanguagesInProcess}
              loading={countryLanguagesInProcess}
              onClick={handleAdd}
            >
              Add language
            </Button>
          </Space>
        </>
      )}
    </Space>
  );
}

export default SettingsCountryLanguagesContainer;
